import { FC, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import CollapsibleArea from '@/components/CollapsibleArea';
import { TBody, TD, TH, THead, Table, TableRow } from '@/components/DesignLibrary/Table';
import { DEFAULT_CURRENCY } from '@typings/common';
import * as format from '@format';
import * as tsClient from '@tsClient';
import SecondaryActions from '@components/DesignLibrary/SecondaryActions';
import useNoodleApi from '@/hooks/useNoodleApi';
import { useToast } from '@hooks';
import { ApiDefinition } from '@noodleApi';
import EditInstallmentModal from '../EditInstallment';
import DeleteInstallmentModal from '../DeleteInstallment';

type Payment =
  & Pick<ApiModels.Payment,
    | 'id'
    | 'serviceAmount'
    | 'type'
    | 'passedProcessingFeesToCustomer'
    | 'paymentStatus'
    | 'amountRefunded'
    | 'creatorCurrency'
    | 'createdAt'
    | 'paymentMethodType'
    | 'last4'
    | 'processingFees'
    | 'notes'
    | 'serviceAmount'
  >
  & {
    person: Pick<ApiModels.Person, 'id' | 'name' | 'email'>;
  };

type CancelEvent = {
  id: string;
  type: 'cancel-event';
  canceledAt: string;
};

type PaymentPlan = Pick<ApiModels.PaymentPlan,
  | 'id'
  | 'amount'
  | 'nextPaymentDate'
  | 'paymentMethodId'
  | 'frequency'
  | 'paymentPlanPayments'
>;

type InstallmentData =
  & Omit<Parameters<typeof EditInstallmentModal>[0]['installmentData'], 'index'>
  & Parameters<typeof DeleteInstallmentModal>[0]['installmentData'];

type Props = {
  payments: Array<Payment | CancelEvent>
  paymentPlan: PaymentPlan | null;
  currency: string;
  invoice: ApiDefinition['/invoices/{id}']['GET']['response'];
  refetchData: () => Promise<void>;
};

const isCancelEvent = (payment: Payment | CancelEvent): payment is CancelEvent => payment.type === 'cancel-event';

function formatPaymentStatus(status: string): JSX.Element {
  switch (status) {
  case 'succeeded':
    return (
      <span className="caption" style={{ color: 'var(--color-success)' }}>
        Succeeded
      </span>
    );
  case 'requires_payment_method':
    return (
      <span className="caption" style={{ color: 'var(--color-warning)' }}>
        Requires payment method
      </span>
    );
  case 'failed':
    return (
      <span className="caption" style={{ color: 'var(--color-error)' }}>
        Failed
      </span>
    );
  case 'pending':
    return <span className="caption">Pending</span>;
  case 'voided':
    return (
      <span className="caption" style={{ color: 'var(--color-error)' }}>
        Voided
      </span>
    );
  default:
    return <span className="caption">{status}</span>;
  }
}

const InvoicePaymentsTable: FC<Props> = ({ payments, paymentPlan, currency, refetchData, invoice }) => {
  const hasRefundedAmounts = payments.some(payment => !isCancelEvent(payment) && payment.amountRefunded > 0);
  const [editInstallmentData,setEditInstallmentData] = useState<(InstallmentData & { index: number}) | null>(null);
  const [deleteInstallmentData,setDeleteInstallmentData] = useState<InstallmentData | null>(null);
  const { getData: deleteInstallmentFn, fetchingState: deleteInstallmentState } = useNoodleApi(tsClient.invoices.deleteInstallment, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error deleting the installment',
    ],
    toastOnError: true,
  });
  const { getData: editInstallmentFn,fetchingState: editInstallmentState } = useNoodleApi(tsClient.invoices.editInstallment, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error editing the installment',
    ],
    toastOnError: true,
  });

  const closeEditInstallmentModal = ():void => {
    setEditInstallmentData(null);
  };
  const submitEditInstallment = async (data:{
    expectedAmount: number;
    expectedDate: string;
    differenceAction: string;
    paymentPlanPaymentId: string;
    paymentPlanId: string;
  }):Promise<void> => {
    await editInstallmentFn({
      ...data,
      invoiceId:invoice.id,
    });
    refetchData();
    setEditInstallmentData(null);
  };
  const closeDeleteInstallmentModal = ():void => {
    setDeleteInstallmentData(null);
  };
  const submitDeleteInstallment = async (data:{
    differenceAction: string;
    paymentPlanPaymentId: string;
    paymentPlanId: string;
  }):Promise<void> => {
    await deleteInstallmentFn({
      invoiceId:invoice.id,
      ...data,
    });
    refetchData();
    setDeleteInstallmentData(null);
  };
  return (
    <>
      {payments.length > 0 && (
        <CollapsibleArea title={`Recent payment${payments.length > 1 ? `s (${payments.length})` : ''}`} caretPosition="left" open>
          <Table>
            <THead>
              <TableRow>
                <TH label="Amount" />
                {hasRefundedAmounts && <TH label="Refunded amount" />}
                <TH label="Date" />
                <TH label="Payment method" />
                <TH label="Status" />
                <TH label="Payor" />
                <TH label="Processing fees" />
                <TH label="Notes" />
              </TableRow>
            </THead>
            <TBody>
              {payments.map(payment => {
                if (isCancelEvent(payment)) {
                  return (
                    <TableRow key={payment.id} state="error">
                      <TD compact label="Payment plan canceled" />
                      {hasRefundedAmounts && <TD compact label="—" />}
                      <TD compact label={format.datetimeLocalTZ.withYear(payment.canceledAt)} />
                      <TD compact label="—" />
                      <TD compact label="—" />
                      <TD compact label="—" />
                      <TD compact label="—" />
                      <TD compact label="—" />
                    </TableRow>
                  );
                }

                const {serviceAmount} = payment;
                const beforeRefundAmount = serviceAmount + (payment.amountRefunded ?? 0);

                return (
                  <TableRow
                    key={payment.id}
                    state={
                      ((payment.paymentStatus === 'failed' || payment.paymentStatus === 'voided') && 'error')
                      || (payment.paymentStatus === 'pending' && 'muted')
                      || (payment.paymentStatus === 'requires_payment_method' && 'warning')
                      || null
                    }
                  >
                    <TD>
                      <p className="caption">
                        <span
                          {...(payment.amountRefunded > 0 && {
                            style: { opacity: 0.5, textDecoration: 'line-through' },
                          })}
                        >
                          {format.price.withCurrency(
                            beforeRefundAmount,
                            payment.creatorCurrency || DEFAULT_CURRENCY,
                          )}
                        </span>
                        {payment.amountRefunded > 0 && (
                          <>
                            {' '}
                            {format.price.withCurrency(
                              serviceAmount,
                              payment.creatorCurrency || DEFAULT_CURRENCY,
                            )}
                          </>
                        )}
                      </p>
                    </TD>
                    {hasRefundedAmounts && (
                      <>
                        {payment.amountRefunded
                          ? <TD compact label={format.price.withCurrency(payment.amountRefunded, payment.creatorCurrency || DEFAULT_CURRENCY)} />
                          : <TD compact label="—" />}
                      </>
                    )}
                    <TD compact label={format.datetimeLocalTZ.withYear(payment.createdAt)} />
                    <TD
                      compact
                      label={
                        (payment.paymentMethodType === 'outside-of-noodle' && 'Payment made outside of Glade')
                        || (payment.last4 && `*${payment.last4}`)
                        || ''
                      }
                    />
                    <TD>{formatPaymentStatus(payment.paymentStatus)}</TD>
                    <TD compact label={payment.person.name || payment.person.email || payment.person.id} />
                    <TD>
                      <div className="caption">
                        {format.price.withCurrency(payment.processingFees, payment.creatorCurrency || DEFAULT_CURRENCY)}
                      </div>
                      <div className="caption">
                        {payment.passedProcessingFeesToCustomer ? '(Paid by customer)' : ''}
                      </div>
                    </TD>
                    <TD compact label={payment.notes || ''} />
                  </TableRow>
                );
              })}
            </TBody>
          </Table>
        </CollapsibleArea>
      )}
      {!!paymentPlan?.paymentPlanPayments?.length && (
        <CollapsibleArea title={`Upcoming payment${paymentPlan?.paymentPlanPayments?.length > 1 ? `s (${paymentPlan?.paymentPlanPayments.length})` : ''}`} caretPosition="left" open>
          <Table>
            <THead>
              <TableRow>
                <TH label="Installments" />
                <TH label="Date due" />
                <TH label="Amount" />
                <TH label="Status" />
                <TH label="Actions" />
              </TableRow>
            </THead>
            <TBody>
              {paymentPlan?.paymentPlanPayments?.map((payment, index) => (
                <TableRow key={payment.id}>
                  <TD label = {`${index+1}`} compact />
                  <TD compact label={format.datetimeLocalTZ.withYear(payment.expectedDate)} />
                  <TD compact label={format.price.withCurrency(payment.expectedAmount, currency)} />
                  <TD label={payment.status === 'upcoming' ? 'Upcoming' : (payment.status === 'retrying' ? 'Retrying' : (payment.status === 'paid' ? 'Pre-Paid' : 'Failed'))} compact />
                  <TD>
                    {(
                      payment?.expectedDate
                      && new Date(payment.expectedDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0)
                    ) && (
                      <SecondaryActions
                        compact
                        ariaLabel='Task Options'
                        position='bottom-right'
                        options={[
                          { destructive: false, value: 'Edit installment' },
                          ...(index < paymentPlan.paymentPlanPayments.length - 1
                            ? [{ destructive: true, value: 'Delete installment' }]
                            : []),
                        ]}
                        onSelect={value =>
                        {
                          if (value === 'Edit installment') {
                            setEditInstallmentData({
                              index,
                              ...payment,
                            });
                          }
                          else if(value==='Delete installment') {
                            setDeleteInstallmentData({
                              ...payment,
                            });
                          }
                        }}
                      />
                    )}
                  </TD>
                </TableRow>
              ))}
            </TBody>
          </Table>
        </CollapsibleArea>
      )}
      {!!paymentPlan?.paymentPlanPayments?.length
      && editInstallmentData?.paymentPlanId
      && <EditInstallmentModal
        fetchingState={editInstallmentState}
        paymentPlanPayments={paymentPlan.paymentPlanPayments}
        invoice={invoice} onClose={closeEditInstallmentModal}
        handleSubmit={submitEditInstallment}
        installmentData={editInstallmentData}
      />}
      {!!paymentPlan?.paymentPlanPayments?.length
      && deleteInstallmentData?.paymentPlanId
      && <DeleteInstallmentModal
        fetchingState={deleteInstallmentState}
        invoice={invoice}
        onClose={closeDeleteInstallmentModal}
        handleSubmit={submitDeleteInstallment}
        installmentData={deleteInstallmentData}
      />}
    </>
  );
};

export default InvoicePaymentsTable;
